import { useMediaQuery } from '@mantine/hooks';
import { DESKTOP_MEDIA_QUERY } from 'lib/constants/ui';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'kapa-sidebar-collapsed';

const readStoredCollapsed = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return (
      window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true'
    );
  } catch {
    return false;
  }
};

const writeStoredCollapsed = (value: boolean): void => {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(value));
  } catch {
    // Ignore storage failures (private mode, quota).
  }
};

interface SidebarUIContextValue {
  /** Desktop rail state, persisted to localStorage. */
  isCollapsed: boolean;
  /** True at >= lg, where the rail/full toggle reflows inline. */
  isDesktop: boolean;
  /** Drawer open state, only meaningful below lg. */
  isMobileOpen: boolean;
  toggleCollapsed: () => void;
  toggleMobile: () => void;
  closeMobile: () => void;
  /** Single control for the header toggle and the keyboard shortcut. */
  toggle: () => void;
}

const SidebarUIContext = createContext<SidebarUIContextValue | null>(null);

export const useSidebarUI = (): SidebarUIContextValue => {
  const context = useContext(SidebarUIContext);
  if (!context) {
    throw new Error('useSidebarUI must be used within a SidebarUIProvider');
  }
  return context;
};

interface SidebarUIProviderProps {
  children: ReactNode;
}

export const SidebarUIProvider = ({ children }: SidebarUIProviderProps) => {
  const [isCollapsed, setIsCollapsed] = useState(readStoredCollapsed);
  // Read synchronously on first paint so the rail/full choice doesn't flash.
  // Defaults to desktop when matchMedia is unavailable (e.g. tests).
  const isDesktop =
    useMediaQuery(DESKTOP_MEDIA_QUERY, true, {
      getInitialValueInEffect: false,
    }) ?? true;
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();

  // The drawer only exists below lg. Close it on desktop and on navigation, so
  // clicking a nav item or a menu entry dismisses the drawer.
  useEffect(() => {
    setIsMobileOpen(false);
  }, [isDesktop, location.pathname]);

  // Persist in the updater rather than a reactive effect: this is the only
  // place isCollapsed changes. The functional updater keeps the callback (and
  // the context value derived from it) identity-stable across toggles.
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      writeStoredCollapsed(next);
      return next;
    });
  }, []);
  const toggleMobile = useCallback(() => setIsMobileOpen((prev) => !prev), []);
  const closeMobile = useCallback(() => setIsMobileOpen(false), []);

  const toggle = useCallback(() => {
    if (isDesktop) {
      toggleCollapsed();
    } else {
      toggleMobile();
    }
  }, [isDesktop, toggleCollapsed, toggleMobile]);

  // Cmd/Ctrl + . mirrors the in-header toggle.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl + . is unreserved on Windows/Linux, so don't swallow the period
      // (e.g. typing "3.14") when focus is in an editable field.
      const target = event.target;
      const inEditable =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable);
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === '.' &&
        !inEditable
      ) {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  const value = useMemo<SidebarUIContextValue>(
    () => ({
      isCollapsed,
      isDesktop,
      isMobileOpen,
      toggleCollapsed,
      toggleMobile,
      closeMobile,
      toggle,
    }),
    [
      isCollapsed,
      isDesktop,
      isMobileOpen,
      toggleCollapsed,
      toggleMobile,
      closeMobile,
      toggle,
    ],
  );

  return (
    <SidebarUIContext.Provider value={value}>
      {children}
    </SidebarUIContext.Provider>
  );
};
